import React, { useState, useEffect, useContext } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend, LineChart, Line
} from 'recharts';
import {
  TrendingUp, TrendingDown, Milk, BarChart3, PieChart as PieChartIcon,
  Activity, Droplets, Sun, Moon, ChevronDown, RefreshCw
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { AuthContext } from '../App';
import apiService from '../utils/api';
import { sortedByName } from '../utils/sortAlpha';
import './home.css';

const TODAY_DATE = new Date().toISOString().split('T')[0];

const colors = {
  primary: '#059669',
  secondary: '#0ea5e9',
  accent: '#f59e0b',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#0ea5e9',
  success: '#10b981',
  cow: '#10b981',
  buffalo: '#312e81',
  morning: '#f59e0b',
  night: '#6366f1',
  text: '#0f172a',
  grid: '#e2e8f0'
};

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color = colors.primary, trend = null, subtitle = null }) => (
  <div className="stat-card">
    <div className="stat-header">
      <div className="stat-icon-wrapper" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={22} />
      </div>
      {trend !== null && (
        <div className={`trend ${trend >= 0 ? 'positive' : 'negative'}`}>
          {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <div className="stat-content">
      <h3 className="stat-value" style={{ color: colors.text }}>{value}</h3>
      <p className="stat-title">{title}</p>
      {subtitle && <small className="stat-subtitle">{subtitle}</small>}
    </div>
  </div>
);

// ─── Chart Card ──────────────────────────────────────────────────────────────
const ChartCard = ({ title, children, className = '', badge = null }) => (
  <div className={`chart-card ${className}`}>
    <div className="chart-card-header">
      <h3 className="chart-title">{title}</h3>
      {badge && <span className="chart-badge">{badge}</span>}
    </div>
    <div className="chart-content">{children}</div>
  </div>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }}>
            {entry.name}: <strong>{typeof entry.value === 'number' ? `${entry.value.toFixed(1)}L` : entry.value}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Dairy Selector ───────────────────────────────────────────────────────────
const DairySelector = ({ dairies, selectedDairy, onChange }) => (
  <div className="dairy-selector-wrapper">
    <label className="dairy-selector-label">
      <Milk size={16} />
      <span>Dairy</span>
    </label>
    <div className="dairy-selector-control">
      <select
        className="dairy-selector"
        value={selectedDairy}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">All Dairies</option>
        {sortedByName(dairies, d => d.name || `Dairy ${d.tabela_id}`).map((d) => (
          <option key={d.tabela_id} value={d.tabela_id}>{d.name || `Dairy ${d.tabela_id}`}</option>
        ))}
      </select>
      <ChevronDown size={14} className="dairy-selector-arrow" />
    </div>
  </div>
);

// ─── Morning / Night breakdown mini-card ─────────────────────────────────────
const SessionCard = ({ session, cowVal, buffaloVal, total, icon: Icon, color }) => (
  <div className="session-card" style={{ borderLeftColor: color }}>
    <div className="session-header">
      <Icon size={16} style={{ color }} />
      <span className="session-label" style={{ color }}>{session}</span>
    </div>
    <div className="session-body">
      <div className="session-row">
        <span className="session-dot" style={{ background: colors.cow }} />
        <span>Cow</span>
        <strong>{cowVal.toFixed(1)}L</strong>
      </div>
      <div className="session-row">
        <span className="session-dot" style={{ background: colors.buffalo }} />
        <span>Buffalo</span>
        <strong>{buffaloVal.toFixed(1)}L</strong>
      </div>
      <div className="session-total">Total: <strong>{total.toFixed(1)}L</strong></div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
function Home() {
  const [currentPage, setCurrentPage] = useState('Home');
  const [dairies, setDairies] = useState([]);
  const [selectedDairy, setSelectedDairy] = useState('all');
  const [dashboardData, setDashboardData] = useState({
    dailyMilkData: [],
    tabelaData: [],
    monthlyTrends: [],
    sessionStats: { morning: { cow: 0, buffalo: 0, total: 0 }, night: { cow: 0, buffalo: 0, total: 0 } },
    summaryStats: null
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const { user } = useContext(AuthContext);

  // ── Fetch dairy list once ──
  useEffect(() => {
    const loadDairies = async () => {
      if (user?.role === 'owner') {
        try {
          const res = await apiService.getTabela();
          setDairies(res.data || []);
        } catch (e) {
          console.warn('Failed to load dairies:', e);
        }
      }
    };
    loadDairies();
  }, [user]);

  // ── Main data fetch ───────────────────────────────────────────────────────
  const fetchDashboardData = async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);

      // Choose the right API call based on selected dairy
      const milkParams = { start_date: dateRange.start, end_date: dateRange.end, per_page: 60 };
      let rawData = [];

      if (selectedDairy !== 'all') {
        const res = await apiService.getTabelaMilkCollection(selectedDairy, milkParams);
        rawData = res.data || [];
      } else {
        const res = await apiService.getDailyMilkCollection(milkParams);
        rawData = res.data || [];
      }

      // Normalise rows → common shape
      const processedMilkData = rawData.map(item => {
        const cowM  = parseFloat(item.cow_morning   || 0);
        const cowN  = parseFloat(item.cow_night     || 0);
        const bufM  = parseFloat(item.buffalo_morning || 0);
        const bufN  = parseFloat(item.buffalo_night  || 0);
        const total = parseFloat(item.total_milk || (cowM + cowN + bufM + bufN));
        return {
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fullDate: item.date,
          cowMorning: cowM,
          cowNight:   cowN,
          bufMorning: bufM,
          bufNight:   bufN,
          cowMilk:    cowM + cowN,
          buffaloMilk: bufM + bufN,
          totalMilk:  total,
          morningTotal: cowM + bufM,
          nightTotal:   cowN + bufN,
        };
      });

      // ── Totals ──
      const totalMilk        = processedMilkData.reduce((s, d) => s + d.totalMilk, 0);
      const totalCowMilk     = processedMilkData.reduce((s, d) => s + d.cowMilk, 0);
      const totalBuffaloMilk = processedMilkData.reduce((s, d) => s + d.buffaloMilk, 0);
      const avgDailyMilk     = processedMilkData.length ? totalMilk / processedMilkData.length : 0;
      const cowMorningTotal  = processedMilkData.reduce((s, d) => s + d.cowMorning, 0);
      const cowNightTotal    = processedMilkData.reduce((s, d) => s + d.cowNight,   0);
      const bufMorningTotal  = processedMilkData.reduce((s, d) => s + d.bufMorning, 0);
      const bufNightTotal    = processedMilkData.reduce((s, d) => s + d.bufNight,   0);
      const morningTotal     = processedMilkData.reduce((s, d) => s + d.morningTotal, 0);
      const nightTotal       = processedMilkData.reduce((s, d) => s + d.nightTotal, 0);

      // ── Trend vs previous period ──
      const mid = Math.floor(processedMilkData.length / 2);
      const firstHalf  = processedMilkData.slice(0, mid).reduce((s, d) => s + d.totalMilk, 0);
      const secondHalf = processedMilkData.slice(mid).reduce((s, d) => s + d.totalMilk, 0);
      const trendPct   = firstHalf ? Math.round(((secondHalf - firstHalf) / firstHalf) * 100) : 0;

      // ── Monthly trends (last 6 months) ──
      const monthlyTrends = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const slice = processedMilkData.filter(item => {
          const id = new Date(item.fullDate);
          return id.getMonth() === d.getMonth() && id.getFullYear() === d.getFullYear();
        });
        monthlyTrends.push({
          month: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          cow:     Math.round(slice.reduce((s, x) => s + x.cowMilk, 0)),
          buffalo: Math.round(slice.reduce((s, x) => s + x.buffaloMilk, 0)),
          total:   Math.round(slice.reduce((s, x) => s + x.totalMilk, 0)),
        });
      }

      // ── Tabela comparison (owner, all dairies view) ──
      let tabelaData = [];
      if (user?.role === 'owner' && selectedDairy === 'all') {
        tabelaData = await Promise.all(
          (dairies || []).slice(0, 8).map(async (t) => {
            try {
              const res = await apiService.getTabelaMilkCollection(t.tabela_id, milkParams);
              return {
                name: t.name || `Dairy ${t.tabela_id}`,
                cow:     Math.round(res.summary?.total_cow_collection     || 0),
                buffalo: Math.round(res.summary?.total_buffalo_collection || 0),
                total:   Math.round(res.summary?.total_collection         || 0),
              };
            } catch {
              return { name: t.name || `Dairy ${t.tabela_id}`, cow: 0, buffalo: 0, total: 0 };
            }
          })
        );
      }

      setDashboardData({
        dailyMilkData: [...processedMilkData].reverse(),
        tabelaData,
        monthlyTrends,
        sessionStats: {
          morning: { cow: cowMorningTotal, buffalo: bufMorningTotal, total: morningTotal },
          night:   { cow: cowNightTotal,   buffalo: bufNightTotal,   total: nightTotal  },
        },
        summaryStats: {
          totalMilk:        Math.round(totalMilk),
          avgDailyMilk:     Math.round(avgDailyMilk * 10) / 10,
          totalCowMilk:     Math.round(totalCowMilk),
          totalBuffaloMilk: Math.round(totalBuffaloMilk),
          activeDays:       processedMilkData.length,
          trend:            trendPct,
        }
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchDashboardData(); }, [dateRange, selectedDairy, user]);

  // ── Derived ──
  const { summaryStats, sessionStats, dailyMilkData, monthlyTrends, tabelaData } = dashboardData;

  const milkTypeData = summaryStats ? [
    { name: 'Cow',     value: summaryStats.totalCowMilk,     color: colors.cow     },
    { name: 'Buffalo', value: summaryStats.totalBuffaloMilk, color: colors.buffalo },
  ] : [];

  const sessionPieData = summaryStats ? [
    { name: 'Morning', value: Math.round(sessionStats.morning.total), color: colors.morning },
    { name: 'Night',   value: Math.round(sessionStats.night.total),   color: colors.night   },
  ] : [];

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-container">
        <Header />
        <div className="main-content">
          <Sidebar onNavigate={setCurrentPage} />
          <div className="page-content">
            <div className="loading-spinner">
              <div className="spinner" />
              <p>Loading dashboard…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar onNavigate={setCurrentPage} />

        <div className="page-content dashboard">

          {/* ── Dashboard Header ── */}
          <div className="dashboard-header">
            <div className="dashboard-title-block">
              <h1>Dashboard</h1>
              <p>Welcome back, {user?.name}!</p>
            </div>

            <div className="dashboard-controls">
              {/* Dairy Picker (owner only) */}
              {user?.role === 'owner' && dairies.length > 0 && (
                <DairySelector
                  dairies={dairies}
                  selectedDairy={selectedDairy}
                  onChange={setSelectedDairy}
                />
              )}

              {/* Date Range */}
              <div className="date-range-selector">
                <input
                  type="date" max={TODAY_DATE}
                  value={dateRange.start}
                  onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}
                />
                <span>to</span>
                <input
                  type="date" max={TODAY_DATE}
                  value={dateRange.end}
                  onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}
                />
              </div>

              {/* Refresh */}
              <button
                className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
                onClick={() => fetchDashboardData(true)}
                title="Refresh"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {/* ── Summary KPI Cards ── */}
          <div className="stats-grid">
            <StatCard
              title="Total Milk Collected"
              value={`${summaryStats?.totalMilk || 0}L`}
              icon={Activity}
              color={colors.primary}
              trend={summaryStats?.trend}
              subtitle={`Avg: ${summaryStats?.avgDailyMilk || 0}L/day · ${summaryStats?.activeDays || 0} days`}
            />
            <StatCard
              title="Total Cow Milk"
              value={`${summaryStats?.totalCowMilk || 0}L`}
              icon={Droplets}
              color={colors.cow}
              subtitle={`${Math.round(((summaryStats?.totalCowMilk || 0) / (summaryStats?.totalMilk || 1)) * 100)}% of total`}
            />
            <StatCard
              title="Total Buffalo Milk"
              value={`${summaryStats?.totalBuffaloMilk || 0}L`}
              icon={BarChart3}
              color={colors.buffalo}
              subtitle={`${Math.round(((summaryStats?.totalBuffaloMilk || 0) / (summaryStats?.totalMilk || 1)) * 100)}% of total`}
            />
            <StatCard
              title="Morning Collection"
              value={`${Math.round(sessionStats?.morning?.total || 0)}L`}
              icon={Sun}
              color={colors.morning}
              subtitle={`Cow: ${Math.round(sessionStats?.morning?.cow || 0)}L · Buf: ${Math.round(sessionStats?.morning?.buffalo || 0)}L`}
            />
            <StatCard
              title="Night Collection"
              value={`${Math.round(sessionStats?.night?.total || 0)}L`}
              icon={Moon}
              color={colors.night}
              subtitle={`Cow: ${Math.round(sessionStats?.night?.cow || 0)}L · Buf: ${Math.round(sessionStats?.night?.buffalo || 0)}L`}
            />
          </div>

          {/* ── Session Breakdown ── */}
          <div className="session-grid">
            <SessionCard
              session="Morning Session"
              cowVal={sessionStats?.morning?.cow || 0}
              buffaloVal={sessionStats?.morning?.buffalo || 0}
              total={sessionStats?.morning?.total || 0}
              icon={Sun}
              color={colors.morning}
            />
            <SessionCard
              session="Night Session"
              cowVal={sessionStats?.night?.cow || 0}
              buffaloVal={sessionStats?.night?.buffalo || 0}
              total={sessionStats?.night?.total || 0}
              icon={Moon}
              color={colors.night}
            />
          </div>

          {/* ── Charts Grid ── */}
          <div className="charts-grid">

            {/* Daily Production – Stacked Bar */}
            <ChartCard title="Daily Milk Production" className="chart-large" badge="Last 30 days">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dailyMilkData.slice(0, 30)} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                  <XAxis dataKey="date" stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} unit="L" />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Legend />
                  <Bar dataKey="cowMilk"     name="Cow"     stackId="a" fill={colors.cow}     radius={[0,0,0,0]} />
                  <Bar dataKey="buffaloMilk" name="Buffalo" stackId="a" fill={colors.buffalo} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Milk Type Distribution Donut */}
            <ChartCard title="Milk Type Split" className="chart-medium">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={milkTypeData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                    {milkTypeData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v}L`]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-chart-legend">
                {milkTypeData.map((e, i) => (
                  <div key={i} className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: e.color }} />
                    <span>{e.name}: <strong>{e.value}L</strong></span>
                  </div>
                ))}
              </div>
            </ChartCard>

            {/* Morning vs Night Donut */}
            <ChartCard title="Morning vs Night Split" className="chart-medium">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={sessionPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                    {sessionPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v}L`]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-chart-legend">
                {sessionPieData.map((e, i) => (
                  <div key={i} className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: e.color }} />
                    <span>{e.name}: <strong>{e.value}L</strong></span>
                  </div>
                ))}
              </div>
            </ChartCard>

            {/* Cow vs Buffalo trend over time */}
            <ChartCard title="Cow & Buffalo Trend" className="chart-large" badge="Daily">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dailyMilkData.slice(0, 30)} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                  <XAxis dataKey="date" stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} unit="L" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="cowMilk"     name="Cow"     stroke={colors.cow}     strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="buffaloMilk" name="Buffalo" stroke={colors.buffalo} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Morning vs Night trend */}
            <ChartCard title="Morning vs Night Collection" className="chart-large" badge="Daily">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dailyMilkData.slice(0, 30)} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                  <XAxis dataKey="date" stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} unit="L" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="morningTotal" name="Morning" stroke={colors.morning} fill={`${colors.morning}30`} strokeWidth={2} />
                  <Area type="monotone" dataKey="nightTotal"   name="Night"   stroke={colors.night}   fill={`${colors.night}25`}   strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Monthly Cow + Buffalo grouped bar */}
            <ChartCard title="Monthly Milk Trends" className="chart-large" badge="6 months">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyTrends} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                  <XAxis dataKey="month" stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} unit="L" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="cow"     name="Cow"     fill={colors.cow}     radius={[4,4,0,0]} />
                  <Bar dataKey="buffalo" name="Buffalo" fill={colors.buffalo} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Tabela Comparison (owner, all-dairy view) */}
            {user?.role === 'owner' && selectedDairy === 'all' && tabelaData.length > 0 && (
              <ChartCard title="Dairy-wise Comparison" className="chart-large" badge="All Dairies">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={tabelaData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={colors.grid} />
                    <XAxis type="number" stroke={colors.text} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} unit="L" />
                    <YAxis dataKey="name" type="category" stroke={colors.text} width={90} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="cow"     name="Cow"     fill={colors.cow}     radius={[0,4,4,0]} />
                    <Bar dataKey="buffalo" name="Buffalo" fill={colors.buffalo} radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;